import Button from 'components/common/buttons/Button';
import { Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import ActionsList from 'components/common/ActionsList';
import CheckmarkBullet from 'components/common/CheckmarkBullet';
import EmailInputField from '../common/EmailInputField';
import { ttsTagRemoval } from 'support/generalUtils';
import TextLabel from 'components/common/TextLabel';

export interface SignUpSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  emailValue: string;
  Title: typeof TextLabel;
  Email: {
    Title: typeof TextLabel;
    Input: typeof EmailInputField;
  };
  Terms: typeof TextLabel;
  ActionsList: typeof ActionsList;
}

const OPT_IN_REF = 'OptIn';

const WIDTH = 684;
const HEIGHT = 522;

const TITLE_FONT_SIZE = 36;
const TITLE_FONT_STYLE = 'italic bold';

const EMAIL_DESC_FONT_SIZE = 22;
const EMAIL_DESC_LINE_HEIGHT = 35;

const ACTIONS_SPACING = 15;

const TERMS_FONT_SIZE = 18;
const TERMS_LINE_HEIGHT = 29;
const BUTTON_HEIGHT = 65;
const BUTTON_FONT_SIZE = 18;

const BASE_BUTTON = {
  type: Button,
  minWidth: WIDTH,
  height: BUTTON_HEIGHT,
  fontSize: BUTTON_FONT_SIZE,
  zIndex: constants.ui.zIndex.foregroundLayer + 2,
};

export default class SignUpSection extends HoverableComponent<SignUpSectionTemplateSpec> {
  private _Title = this.getByRef('Title')!;
  private _Email = this.getByRef('Email')!;
  private _EmailTitle = this._Email.getByRef('Title')!;
  private _Input = this._Email.getByRef('Input')!;
  private _Terms = this.getByRef('Terms')!;
  private _ActionsList = this.getByRef('ActionsList')!;
  private _OptInCheckmark = this._ActionsList.getItemByRef(
    OPT_IN_REF,
  ) as CheckmarkBullet;

  set emailValue(value: string) {
    this._Input.input = value;
  }

  get announce() {
    return [
      this._Title.text?.text,
      this._EmailTitle.text?.text,
      ttsTagRemoval(this._Terms.text?.text ?? ''),
    ];
  }

  static override _template(): Lightning.Component.Template<SignUpSectionTemplateSpec> {
    return {
      w: WIDTH,
      h: HEIGHT,
      flex: {
        direction: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      Title: {
        type: TextLabel,
        w: WIDTH, // Italic font gets cut off without a larger than automatic width
        text: {
          text: translate('account.signin.remoteTitle'),
          textAlign: 'center',
          fontSize: TITLE_FONT_SIZE,
          fontStyle: TITLE_FONT_STYLE,
        },
      },
      Email: {
        flex: { direction: 'column' },
        Title: {
          type: TextLabel,
          w: WIDTH,
          text: {
            text: translate('account.signup.createAccount'),
            textAlign: 'center',
            lineHeight: EMAIL_DESC_LINE_HEIGHT,
            fontSize: EMAIL_DESC_FONT_SIZE,
          },
        },
        Input: {
          type: EmailInputField,
          passSignals: {
            $onHover: '$onHover',
            $emailInputSelected: '$resetToEmailInput',
          },
          signals: { $onHover: '$onHover' },
          zIndex: constants.ui.zIndex.foregroundLayer + 2,
        },
      },
      Terms: {
        type: TextLabel,
        w: WIDTH,
        text: {
          text: translate('account.signup.termsDescription'),
          advancedRenderer: true,
          lineHeight: TERMS_LINE_HEIGHT,
          fontSize: TERMS_FONT_SIZE,
        },
      },
      ActionsList: {
        flex: {
          direction: 'column',
        },
        type: ActionsList,
        spacing: ACTIONS_SPACING,
        items: [
          {
            ref: OPT_IN_REF,
            type: CheckmarkBullet,
            label: translate('account.signup.optInCheckmark'),
            minWidth: WIDTH,
            height: BUTTON_HEIGHT,
            fontSize: BUTTON_FONT_SIZE,
            action: '$optInToggled',
            passSignals: { $optInToggled: true },
            zIndex: constants.ui.zIndex.foregroundLayer + 2,
            isSelected: false,
            signals: {
              $onHover: '$onHover',
            },
          },
          {
            ...BASE_BUTTON,
            label: translate('account.signup.signUpLinkLabel'),
            action: '$onSignUpLink',
            passSignals: {
              $onSignUpLink: true,
            },
            signals: {
              $onSignUpLink: '$onSignUpLink',
              $onHover: '$onHover',
            },
          },
          {
            ...BASE_BUTTON,
            label: translate('account.signup.createPasswordLabel'),
            action: '$onCreatePassword',
            passSignals: {
              $onCreatePassword: true,
            },
            signals: {
              $onHover: '$onHover',
            },
          },
        ],
        passSignals: {
          $onCreatePassword: true,
        },
        signals: {
          $onHover: true,
          $optInToggled: true,
          $onSignUpLink: true,
          $onCreatePassword: '$onCreatePassword',
        },
      },
    };
  }

  override _onActive() {
    this._setState('ActionsList');
  }

  private $optInToggled(event: any) {
    this._OptInCheckmark.isSelected = !this._OptInCheckmark.isSelected;
  }

  private $onSignUpLink(event: any) {
    this.signal('$onSignUpLink', {
      email: this._Input.input,
      optIn: this._OptInCheckmark.isSelected,
    });
  }

  private $onCreatePassword(event: any) {
    this.signal('$onCreatePassword', this._Input.input);
  }

  private $onHover(target: any) {
    let state = null;

    if (target.id === this._Input.id) {
      state = 'Email';
    } else if (target.id === this._ActionsList.id) {
      state = 'ActionsList';
    }

    if (state) {
      this._setState(state);
      this.signal('$onSignUpHovered');
    }
  }

  static override _states() {
    return [
      class Email extends this {
        override _handleDown() {
          this._setState('ActionsList');
        }

        override _getFocused() {
          return this._Input;
        }
      },
      class ActionsList extends this {
        override _handleUp() {
          this._setState('Email');
        }
        override _getFocused() {
          return this._ActionsList;
        }
      },
    ];
  }
}
